import { isProductionStep, weekDayStartToNumber } from '@seedplanner/shared-utils';
// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Skeleton, Typography, useMediaQuery } from '../../lib/base';
import type { CalendarSettings } from '../../pages/Calendar';
import type { Theme } from '../../theme/Theme';
import { compareTasks } from '../../utils/sortTasks';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { groupTasksWithStatus } from '../task/groupTasks';
import { TaskBar, type TaskData, type TaskGroup } from './EventBar';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const renderEvents = ({
  taskEvents,
  settings,
}: { readonly taskEvents: TaskData[]; readonly settings: CalendarSettings }) => {
  // const plantingEvents = eventsData.plantingsPerDay.get(dayKey) ?? [];
  // const orderEvents = eventsData.ordersPerDay.get(dayKey) ?? [];
  // const orderCount = orderEvents.length;
  // const showOrderBar = orderCount > 0 && settings.showOrders;
  // const orderEventBar = showOrderBar ? (
  //   <EventBar isCompleted={false} caption={`${orderCount} Orders`} color="#673ab7" />
  // ) : null;

  const groupedTasks: readonly TaskGroup[] = [...groupTasksWithStatus(taskEvents).values()].map((group) => ({
    ...group,
    id: group.tasks[0].id,
    trays: Math.ceil(group.traysNotRounded),
  }));
  // Group tasks by task name or task ID
  const groupedTasksByTaskName = new Map<string, { readonly groups: TaskGroup[]; readonly isForCrop: boolean }>();
  for (const task of groupedTasks) {
    const isForCrop = task.tasks[0].crop != null;
    const key = task.tasks[0].task_name ?? task.id;
    const maybeExisting = groupedTasksByTaskName.get(key);
    if (maybeExisting === undefined) {
      groupedTasksByTaskName.set(key, { groups: [task], isForCrop });
    } else {
      groupedTasksByTaskName.set(key, {
        groups: [...maybeExisting.groups, task],
        isForCrop,
      });
    }
  }

  const groupedTasksByName = Array.from(groupedTasksByTaskName.entries());
  const filteredTasks = groupedTasksByName
    .filter(([_key, grouping]) => {
      return settings.showCompletedTasks ? grouping.groups : !grouping.groups.every((group) => group.isCompleted);
    })
    .filter(([_key, grouping]) => {
      const taskName = grouping.groups[0].tasks[0].task_name;
      const isProductionTask = isProductionStep(taskName);

      // If it's a production task, check showProductionTasks
      // If it's a non-production task, check showNonProductionTasks
      return isProductionTask ? settings.showProductionTasks : settings.showNonProductionTasks;
    });

  filteredTasks.sort(([_key, a], [__key, b]) => compareTasks(a.groups[0].tasks[0], b.groups[0].tasks[0]));

  const renderableEvents = filteredTasks.map(([_key, grouping]) => (
    <TaskBar key={grouping.groups[0].tasks[0].id} taskGroups={grouping.groups} isForCrop={grouping.isForCrop} />
  ));

  return renderableEvents;
};

export const DayCell = React.memo(
  ({
    day,
    index,
    isLastRow,
    isSelectedDay,
    onClickDay,
    showSkeletons,
    rows,
    taskData,
    settings,
    isPrevMonth,
    isNextMonth,
    shouldSetParams,
  }: {
    readonly day: dayjs.Dayjs;
    readonly index: number;
    readonly isLastRow: boolean;
    readonly onClickDay: (day: dayjs.Dayjs, isSelectedDay: boolean, shouldSetParams: boolean) => void;
    readonly isSelectedDay: boolean;
    readonly shouldSetParams: boolean;
    readonly showSkeletons: boolean;
    readonly rows: number;
    readonly taskData: TaskData[];
    readonly settings: CalendarSettings;
    readonly isPrevMonth: boolean;
    readonly isNextMonth: boolean;
  }) => {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { timezone, weekStartPreference } = useUserContext();
    const firstDayOfWeekNum = weekDayStartToNumber(weekStartPreference);

    const eventContainerRef = React.useRef<HTMLDivElement>(null);
    const [visibleEvents, setVisibleEvents] = React.useState<React.ReactNode[]>([]);
    const [hiddenCount, setHiddenCount] = React.useState(0);

    const isToday = day.isSame(dayjs().tz(timezone), 'day');

    const isFirstDayOfMonth = day.date() === 1;
    const isLastDayOfMonth = day.date() === day.daysInMonth();
    // Display month if it's the adjacent month on either side
    const isLastDayOfPrevMonth = isPrevMonth && isLastDayOfMonth;
    const isFirstDayOfNextMonth = isNextMonth && isFirstDayOfMonth;
    // To match the border radius of the background card component:
    // Index 28 should have border radius on bottom left
    const shouldHaveBottomLeftRadius = index === 28;
    // Index 33 should have border radius on bottom right
    const shouldHaveBottomRightRadius = index === 34;
    // No bottom border on last 7 days of calendar
    const shouldHaveBorderBottom = !isLastRow;
    // No right border on last day of each week
    const shouldHaveBorderRight = day.day() !== (6 + firstDayOfWeekNum) % 7;

    const renderableEvents = React.useMemo(
      () => renderEvents({ taskEvents: taskData, settings }),
      [taskData, settings],
    );

    const calculateVisibleEvents = React.useCallback(() => {
      const container = eventContainerRef.current;
      if (!container) {
        return;
      }

      const containerHeight = container.clientHeight;
      const eventHeight = isMobile ? 10 : 24; // Height of event bar + margin
      const maxEvents = Math.floor(containerHeight / eventHeight);

      setVisibleEvents(renderableEvents.slice(0, maxEvents));
      setHiddenCount(renderableEvents.length - maxEvents);
    }, [renderableEvents, isMobile]);

    React.useEffect(() => {
      calculateVisibleEvents();

      const resizeObserver = new ResizeObserver(calculateVisibleEvents);
      if (eventContainerRef.current) {
        resizeObserver.observe(eventContainerRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [calculateVisibleEvents]);

    return (
      <Box
        onClick={() => onClickDay(day, isSelectedDay, shouldSetParams)}
        sx={{
          px: 0.5,
          py: 0.25,
          borderColor: (t) => t.palette.divider,
          borderRight: (t) => `1px solid ${shouldHaveBorderRight ? t.palette.divider : 'transparent'}`,
          borderLeft: 'none',
          borderBottom: (t) => `1px solid ${shouldHaveBorderBottom ? t.palette.divider : 'transparent'}`,
          borderTop: 'none',
          borderRadius: shouldHaveBottomLeftRadius ? '0 0 0 4px' : shouldHaveBottomRightRadius ? '0 0 4px 0' : '0',
          containerName: 'calendar-day',
          containerType: 'inline-size',
        }}
      >
        <Box
          sx={{
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="body2"
            component="span"
            display="block"
            align="center"
            sx={{
              color: isToday ? 'white' : undefined,
              backgroundColor: isToday ? 'rgb(18, 90, 184)' : undefined,
              border: isSelectedDay && !isToday ? '1px solid rgb(18, 90, 184)' : '1px solid transparent',
              borderRadius: isToday || isSelectedDay ? '50%' : undefined,
              height: isToday || isSelectedDay ? '22px' : undefined,
              width: isToday || isSelectedDay ? '24px' : undefined,
            }}
          >
            {day.format(isLastDayOfPrevMonth || isFirstDayOfNextMonth ? 'MMM D' : 'D')}
          </Typography>
        </Box>
        {showSkeletons ? (
          <Box
            sx={{
              height: { sm: `calc(70vh / ${rows})`, xs: '80px' },
            }}
          >
            <VisibleAtContainer
              containerName="month-calendar"
              up={<Skeleton variant="text" width="100%" height={24} />}
              down={<Skeleton variant="text" width="100%" height={10} />}
            />
            <VisibleAtContainer
              containerName="month-calendar"
              up={<Skeleton variant="text" width="100%" height={24} />}
              down={<Skeleton variant="text" width="100%" height={10} />}
            />
            <VisibleAtContainer
              containerName="month-calendar"
              up={<Skeleton variant="text" width="100%" height={24} />}
              down={<Skeleton variant="text" width="100%" height={10} />}
            />
          </Box>
        ) : (
          <Box
            sx={{
              height: { sm: `calc(70vh / ${rows})`, xs: '80px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box
              id="event-container"
              ref={eventContainerRef}
              sx={{
                overflowY: 'hidden',
                flexGrow: 1,
                '@container calendar-day (max-width: 80px)': {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                },
              }}
            >
              {visibleEvents}
            </Box>
            {hiddenCount > 0 && (
              <VisibleAtContainer
                containerName="month-calendar"
                up={
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      padding: '2px 4px',
                    }}
                  >
                    {`+${hiddenCount} more`}
                  </Typography>
                }
                down={
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      padding: '2px 4px',
                    }}
                  >
                    {`+${hiddenCount}`}
                  </Typography>
                }
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.isSelectedDay !== nextProps.isSelectedDay) {
      return false;
    }
    if (prevProps.showSkeletons !== nextProps.showSkeletons) {
      return false;
    }
    if (!prevProps.day.isSame(nextProps.day, 'day')) {
      return false;
    }
    if (prevProps.isLastRow !== nextProps.isLastRow) {
      return false;
    }
    if (prevProps.settings !== nextProps.settings) {
      return false;
    }
    // For some reason empty day cells are re-rendered
    // This hack prevents this unnecessary re-render
    if (
      prevProps.taskData.length !== 0 &&
      nextProps.taskData.length !== 0 &&
      prevProps.taskData !== nextProps.taskData
    ) {
      return false;
    }
    if (prevProps.index !== nextProps.index) {
      return false;
    }
    if (prevProps.rows !== nextProps.rows) {
      return false;
    }
    if (prevProps.isPrevMonth !== nextProps.isPrevMonth) {
      return false;
    }
    if (prevProps.isNextMonth !== nextProps.isNextMonth) {
      return false;
    }
    if (prevProps.onClickDay !== nextProps.onClickDay) {
      return false;
    }

    // Return true means that the component will NOT be re-rendered
    return true;
  },
);
